import React, { useEffect } from 'react';
import { Sparkles, X, CheckCircle2 } from 'lucide-react';

/**
 * NouveautesModal — affiche la liste des nouveautés (changelog).
 *
 * Props :
 *   - entries : tableau d'entrées { version, date, titre, items[] }
 *   - onClose : callback de fermeture
 *
 * Présentation : modal centré, thème sombre cohérent avec l'app,
 * lisible sur mobile (scroll interne si beaucoup de contenu).
 */
export default function NouveautesModal({ entries = [], onClose }) {
  // Fermeture au clavier (Échap)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!entries.length) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Nouveautés de l'application"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[85vh] flex flex-col bg-gray-900 border border-yellow-600/50 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-800 bg-gradient-to-r from-yellow-500/15 to-transparent">
          <Sparkles size={22} className="text-yellow-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-white">Nouveautés de l'application</h2>
            <p className="text-xs text-gray-400">Voici ce qui a changé.</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="p-2 -mr-1 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Corps : liste des versions */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {entries.map((e) => (
            <section key={e.version}>
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <h3 className="text-sm font-semibold text-yellow-300">{e.titre}</h3>
                <span className="text-[11px] text-gray-500 flex-shrink-0">{e.date}</span>
              </div>
              <ul className="space-y-2">
                {e.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-200 leading-relaxed">
                    <CheckCircle2 size={16} className="text-yellow-500/80 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {/* Pied : bouton de fermeture */}
        <div className="px-5 py-4 border-t border-gray-800">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 text-sm font-semibold bg-yellow-500 text-black rounded-xl hover:bg-yellow-400 transition-colors"
          >
            J'ai compris
          </button>
        </div>
      </div>
    </div>
  );
}
